import { useState, useEffect, useRef, useCallback } from 'react';
import { loadCachedPeers } from '../components/FailoverBanner.jsx';

const CHECK_INTERVAL = 10000; // probe home node every 10s
const FAIL_THRESHOLD = 2; // consecutive failures before failover
const HOME_RETRY_INTERVAL = 30000; // retry home node every 30s during failover
const PEER_PORT = 8080;

/**
 * useApiBase — self-monitoring hook that tracks the active API base URL.
 *
 * Periodically probes the home node's /api/capabilities. After consecutive
 * failures, races cached peer IPs to find a responsive node and switches
 * the API base URL to that peer. Periodically retries the home node and
 * switches back when available.
 *
 * Returns { apiBase, activePeer }.
 */
export default function useApiBase() {
  const [apiBase, setApiBase] = useState('');
  const [activePeer, setActivePeer] = useState(null);
  const failCountRef = useRef(0);
  const probingRef = useRef(false);
  const intervalRef = useRef(null);

  const probeAndFailover = useCallback(async () => {
    if (probingRef.current) return;
    probingRef.current = true;
    const peers = loadCachedPeers();
    if (peers.length === 0) {
      probingRef.current = false;
      return;
    }

    const candidates = [];
    for (const peer of peers) {
      for (const addr of (peer.addresses || [])) {
        if (!addr.ip) continue;
        // Skip Tailscale CGNAT addresses
        const parts = addr.ip.split('.');
        if (parts.length === 4 && parts[0] === '100') {
          const second = parseInt(parts[1], 10);
          if (second >= 64 && second <= 127) continue;
        }
        candidates.push({
          ip: addr.ip,
          nodeId: peer.node_id || peer.name || addr.ip,
          url: `http://${addr.ip}:${PEER_PORT}`,
        });
      }
    }

    if (candidates.length === 0) {
      probingRef.current = false;
      return;
    }

    try {
      const controller = new AbortController();
      const winner = await Promise.any(
        candidates.map(async (c) => {
          const resp = await fetch(`${c.url}/api/capabilities`, {
            signal: controller.signal,
            cache: 'no-store',
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          controller.abort();
          return c;
        })
      );
      setApiBase(winner.url);
      setActivePeer({ nodeId: winner.nodeId, ip: winner.ip });
      failCountRef.current = 0;
    } catch {
      // All peers unreachable
    } finally {
      probingRef.current = false;
    }
  }, []);

  useEffect(() => {
    async function check() {
      if (activePeer) {
        // During failover: try home node periodically
        try {
          const resp = await fetch('/api/capabilities', {
            cache: 'no-store',
            signal: AbortSignal.timeout(5000),
          });
          if (resp.ok) {
            setApiBase('');
            setActivePeer(null);
            failCountRef.current = 0;
          }
        } catch {
          // Home still down
        }
      } else {
        // Normal mode: monitor home node health
        try {
          const resp = await fetch('/api/capabilities', {
            cache: 'no-store',
            signal: AbortSignal.timeout(5000),
          });
          if (resp.ok) {
            failCountRef.current = 0;
          } else {
            failCountRef.current++;
          }
        } catch {
          failCountRef.current++;
        }
        if (failCountRef.current >= FAIL_THRESHOLD) {
          probeAndFailover();
        }
      }
    }

    const interval = activePeer ? HOME_RETRY_INTERVAL : CHECK_INTERVAL;
    intervalRef.current = setInterval(check, interval);
    return () => clearInterval(intervalRef.current);
  }, [activePeer, probeAndFailover]);

  return { apiBase, activePeer };
}
